import { ResponseError, Cart, nativeAppHeaders } from '@ynap/api-utils'
import { timeout, delay } from '@ynap/promise-utils'
import { SET_SHIPPING_METHOD_REQUEST, SET_SHIPPING_METHOD_SUCCESS, SET_SHIPPING_METHOD_ERROR } from '../constants/ActionTypes'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getPackagingOptionsStepInternal } from '../selectors/getPackagingOptionsStep'
import { checkRectificationOnSuccess, checkRectificationOnError } from '../actions/cartRectification'
import { checkGuestIdentityError } from './checkGuestIdentityError'
import { getLocale } from '@ynap/cart-state'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { getPackagingOptionRedirect } from './setShippingAddress'

export function setShippingMethodRequest() {
    return {
        type: SET_SHIPPING_METHOD_REQUEST,
    }
}

export function setShippingMethodSuccess(payload) {
    return {
        type: SET_SHIPPING_METHOD_SUCCESS,
        payload,
    }
}

export function setShippingMethodError(error) {
    return {
        type: SET_SHIPPING_METHOD_ERROR,
        error,
    }
}

export default function setShippingMethod(values, redirect, signatureRequired, isPostSplit, lineItems = [], groupedShippingOptions = [], paymentPath = '') {
    return async (dispatch, getState, fetch) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const clientTimeout = getTimeout(state)
        const { packagingOptions = [], currentPackagingOption = {} } = getPackagingOptionsStepInternal(state, {})

        const [shippingOptions, jitShippingOptions] = groupedShippingOptions
        const { shippingOptionId, shippingOptionIdJit, nominatedSlot } = values

        const orderItem = lineItems.map(({ isJIT, orderItemId }) => ({
            orderItemId: orderItemId?.toString(),
            xitem_ffmCenterName: isJIT ? jitShippingOptions?.[0]?.fulfillmentCenterName : shippingOptions?.[0]?.fulfillmentCenterName,
            shipModeId: `${isJIT ? shippingOptionIdJit : shippingOptionId}`,
            ...(!isJIT && nominatedSlot ? { requestedShipDate: nominatedSlot } : {}),
        }))
        const signature = signatureRequired ? values.signatureRequired : undefined
        const isOmniStockFormat = isPostSplit && shippingOptions?.length > 0 && jitShippingOptions?.length > 0

        const body = {
            addressId: '0', // zero must be passed if addressId is unknown, or already set and you don't want to change it. See API.
            x_signatureRequired: signature,
            ...(isOmniStockFormat
                ? {
                      orderItem,
                      shipAsComplete: 'N',
                  }
                : {
                      shipModeId: `${shippingOptionId}`,
                      ...(nominatedSlot ? { requestedShipDate: nominatedSlot } : {}),
                  }),
        }

        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        const request = Cart.setShippingInfo({ body, ...watsonConfig, langId, additionalHeaders })

        dispatch(setShippingMethodRequest())

        const promise = delay(timeout(fetch(request), clientTimeout), getDelay(state))
            .then((response) => {
                if (!response.ok) {
                    throw new ResponseError(response)
                }
                return response.json().catch(() => {}) // Ignore invalid JSON, handled by fallback 🤡
            })
            .then((data) => {
                // Workaround for empty WCS response 🤡
                if (!data || !data.shoppingCart) {
                    const cartRequest = Cart.getCart({ ...watsonConfig, langId, additionalHeaders })
                    return timeout(fetch(cartRequest), clientTimeout).then((response) => {
                        if (!response.ok) {
                            throw new ResponseError(response)
                        }
                        return response.json().then((shoppingCart) => ({ shoppingCart }))
                    })
                }
                return data
            })

        try {
            const data = await promise

            const redirectPath = getPackagingOptionRedirect(packagingOptions, currentPackagingOption, redirect, paymentPath)

            dispatch(setShippingMethodSuccess(data))
            checkRectificationOnSuccess(dispatch, data, redirectPath)
            return data
        } catch (error) {
            dispatch(setShippingMethodError(error))
            checkRectificationOnError(error, dispatch)
            checkGuestIdentityError(error, dispatch, state)
            throw error
        }
    }
}
